/**
 * Device-class probe for ZcashWallet.
 *
 * Used to decide whether to surface the "your device may be slow"
 * warning to the user at first launch, and to ship a one-shot
 * telemetry event so we can see in aggregate whether iPhone 11 /
 * budget Android is a meaningful share of installs (and therefore
 * whether chunked proving is worth building).
 *
 * Probes (all client-side, ~2 ms total):
 *   - WebAssembly SIMD availability (Safari ≥ 16.4, Chrome ≥ 91)
 *   - navigator.deviceMemory (Chrome only; null elsewhere)
 *   - performance.memory.jsHeapSizeLimit (Chromium-only; null elsewhere)
 *   - SharedArrayBuffer presence (needs Cross-Origin-Isolation headers)
 *   - navigator.storage.estimate() — free space + quota
 *
 * Scoring:
 *   "weak"     — at risk of OOM or 15+ s proofs (no SIMD + ≤ 4 GB RAM,
 *                or storage quota under 200 MB)
 *   "ok"       — should be fine; no warning shown
 *   "strong"   — modern phone / desktop; no warning
 *
 * Failures during probing are non-fatal: we report `null` for the
 * field and roll up to "ok" if we can't determine class.
 */

export interface DeviceProbe {
  wasmSimd: boolean | null;
  sharedArrayBuffer: boolean;
  deviceMemoryGb: number | null;
  jsHeapLimitMb: number | null;
  storageQuotaMb: number | null;
  storageAvailableMb: number | null;
  userAgent: string;
}

export type DeviceClass = "weak" | "ok" | "strong";

export interface DeviceAssessment {
  probe: DeviceProbe;
  class: DeviceClass;
  /** Short human-readable reason; surfaced in the UI banner. */
  reason: string | null;
}

// ─── Probe ────────────────────────────────────────────────────────────────

/**
 * 6 bytes that decode as a minimal WASM module using SIMD.v128.const.
 * Compiles → SIMD available; throws → not.
 */
function detectWasmSimd(): boolean | null {
  try {
    // Tiny SIMD test module from the v8 / wasm test suite. Decodes if
    // the runtime supports the SIMD proposal.
    const bytes = new Uint8Array([
      0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10, 10,
      1, 8, 0, 65, 0, 253, 15, 253, 98, 11,
    ]);
    return WebAssembly.validate(bytes);
  } catch {
    return null;
  }
}

export async function probeDevice(): Promise<DeviceProbe> {
  const nav: Navigator & { deviceMemory?: number } =
    typeof navigator !== "undefined" ? navigator : ({} as Navigator);

  const perfMemory =
    typeof performance !== "undefined" &&
    (performance as Performance & {
      memory?: { jsHeapSizeLimit?: number };
    }).memory;

  let storageQuotaMb: number | null = null;
  let storageAvailableMb: number | null = null;
  try {
    if (nav.storage?.estimate) {
      const est = await nav.storage.estimate();
      if (est.quota != null) storageQuotaMb = Math.round(est.quota / 1e6);
      if (est.usage != null && est.quota != null) {
        storageAvailableMb = Math.round((est.quota - est.usage) / 1e6);
      } else if (est.quota != null) {
        storageAvailableMb = Math.round(est.quota / 1e6);
      }
    }
  } catch {
    /* probe is best-effort */
  }

  return {
    wasmSimd: detectWasmSimd(),
    sharedArrayBuffer: typeof SharedArrayBuffer !== "undefined",
    deviceMemoryGb: typeof nav.deviceMemory === "number" ? nav.deviceMemory : null,
    jsHeapLimitMb:
      perfMemory && perfMemory.jsHeapSizeLimit != null
        ? Math.round(perfMemory.jsHeapSizeLimit / 1e6)
        : null,
    storageQuotaMb,
    storageAvailableMb,
    userAgent: nav.userAgent ?? "",
  };
}

/**
 * Roll up the raw probe into a device class + optional warning reason.
 *
 * Failure modes we score against:
 *
 *  - No SIMD → bls12-381 ops 2-3× slower. Single proof on a phone
 *              goes from 3-5 s to 8-15 s.
 *  - ≤ 4 GB device memory → iOS WKWebView cap puts us near OOM during
 *                            spend proof. We can't directly read iOS
 *                            but deviceMemory <= 4 on Android tracks
 *                            the same generation of hardware.
 *  - Storage quota < 200 MB → 50 MB of canonical params + 26 MB
 *                             compressed cache + IDB metadata eats
 *                             most of the budget. Cut-off chosen so
 *                             users without enough headroom see a
 *                             clear warning before they create the
 *                             wallet.
 */
export function assessDevice(probe: DeviceProbe): DeviceAssessment {
  const issues: string[] = [];

  if (probe.wasmSimd === false) {
    issues.push("no WASM SIMD");
  }
  if (probe.deviceMemoryGb != null && probe.deviceMemoryGb <= 4) {
    issues.push(`${probe.deviceMemoryGb} GB RAM`);
  }
  if (probe.storageQuotaMb != null && probe.storageQuotaMb < 200) {
    issues.push(`only ${probe.storageQuotaMb} MB storage available`);
  }

  if (issues.length === 0) {
    // Heuristic: deviceMemory ≥ 8 OR jsHeapLimit ≥ 2 GB → "strong"
    const strongRam =
      (probe.deviceMemoryGb != null && probe.deviceMemoryGb >= 8) ||
      (probe.jsHeapLimitMb != null && probe.jsHeapLimitMb >= 2000);
    return {
      probe,
      class: strongRam ? "strong" : "ok",
      reason: null,
    };
  }
  return {
    probe,
    class: "weak",
    reason: issues.join(", "),
  };
}
